import { promises as fs } from 'fs';
import { join } from 'path';
import { homedir } from 'os';
import { Jai1Error } from '../errors/index.js';

export interface ErrorLogContext {
  command?: string;
  cwdBase?: string;
  details?: unknown;
}

export interface ErrorLogEntry {
  timestamp: string;
  command?: string;
  cwdBase?: string;
  name: string;
  message: string;
  stack?: string;
  exitCode?: number;
  details?: unknown;
}

export interface ReadForSubmitResult {
  files: Array<{ path: string; entry: ErrorLogEntry; bytes: number }>;
  totalBytes: number;
}

export interface ReadLogResult {
  entry: ErrorLogEntry;
  bytes: number;
}

export class ErrorLogService {
  private readonly jai1Dir: string;
  private readonly errorDir: string;

  constructor() {
    this.jai1Dir = join(homedir(), '.jai1');
    this.errorDir = join(this.jai1Dir, 'errors');
  }

  getErrorDir(): string {
    return this.errorDir;
  }

  async logError(error: unknown, context: ErrorLogContext = {}): Promise<string | null> {
    const err = error instanceof Error ? error : new Error(String(error));
    const nowIso = new Date().toISOString();
    const filenameSafe = nowIso.replace(/[:.]/g, '-');

    const entry: ErrorLogEntry = {
      timestamp: nowIso,
      command: context.command,
      cwdBase: context.cwdBase,
      name: err.name || 'Error',
      message: err.message || 'Unknown error',
      stack: err.stack,
      exitCode: error instanceof Jai1Error ? error.exitCode : undefined,
      details: context.details,
    };

    try {
      await fs.mkdir(this.errorDir, { recursive: true, mode: 0o700 });
      const filePath = join(this.errorDir, `error-${filenameSafe}.json`);
      await fs.writeFile(filePath, JSON.stringify(entry, null, 2), { mode: 0o600 });

      // Best-effort rotation
      await this.enforceRotation(20);
      return filePath;
    } catch {
      // Never block CLI on logging
      return null;
    }
  }

  async list(): Promise<string[]> {
    try {
      const entries = await fs.readdir(this.errorDir, { withFileTypes: true });
      return entries
        .filter((e) => e.isFile() && e.name.endsWith('.json'))
        .map((e) => join(this.errorDir, e.name))
        .sort()
        .reverse();
    } catch {
      return [];
    }
  }

  async readLog(path: string): Promise<ReadLogResult | null> {
    try {
      const content = await fs.readFile(path, 'utf-8');
      const bytes = Buffer.byteLength(content, 'utf-8');
      const entry = JSON.parse(content) as ErrorLogEntry;
      return { entry, bytes };
    } catch {
      return null;
    }
  }

  async readForSubmit(options: { limitCount: number; limitBytes: number }): Promise<ReadForSubmitResult> {
    const paths = await this.list();
    const selected = paths.slice(0, options.limitCount);

    const result: ReadForSubmitResult = { files: [], totalBytes: 0 };

    for (const path of selected) {
      const read = await this.readLog(path);
      if (!read) continue;

      if (result.totalBytes + read.bytes > options.limitBytes) {
        break;
      }

      result.files.push({ path, entry: read.entry, bytes: read.bytes });
      result.totalBytes += read.bytes;
    }

    return result;
  }

  async clear(filePaths: string[]): Promise<void> {
    await Promise.all(
      filePaths.map(async (p) => {
        try {
          await fs.rm(p, { force: true });
        } catch {
          // ignore
        }
      })
    );
  }

  private async enforceRotation(maxFiles: number): Promise<void> {
    try {
      const paths = await this.list();
      const toDelete = paths.slice(maxFiles);
      if (toDelete.length === 0) return;
      await this.clear(toDelete);
    } catch {
      // ignore
    }
  }
}
